import { useCallback, useEffect, useRef } from 'react';
import { RouterProviderProps } from 'react-router-dom';
import { createConfirmation } from 'react-confirm';
import { CallableConfirmDialog } from '@converge/ui';

type Listener = () => boolean | Promise<boolean>;
const listeners: Listener[] = [];

const beforeUnload = (e: BeforeUnloadEvent) => {
  e.preventDefault();
  e.returnValue = '';
};

const blockNavigation = (fn: Listener) => {
  if (listeners.length === 0) {
    window.addEventListener('beforeunload', beforeUnload, { capture: true });
  }
  listeners.push(fn);

  return () => {
    const index = listeners.indexOf(fn);
    listeners.splice(index, 1);
    if (listeners.length === 0) {
      window.removeEventListener('beforeunload', beforeUnload, {
        capture: true,
      });
    }
  };
};

const useBlocker = (enabled: boolean, blocker: Listener) => {
  useEffect(() => {
    if (!enabled) return;
    return blockNavigation(blocker);
  }, [blocker, enabled]);
};

const confirm = createConfirmation(CallableConfirmDialog);

export const usePrompt = (message: string, enabled: boolean = true) => {
  const confirmNavigation = useCallback(
    async () =>
      Boolean(
        await confirm({
          confirmation: message,
        })
      ),
    [message]
  );

  useBlocker(enabled, confirmNavigation);
};

export const usePromptNavigationListener = ({
  router,
}: RouterProviderProps) => {
  const renderCounter = useRef(0);

  if (renderCounter.current === 1) {
    const _navigate = router.navigate.bind(router);
    router.navigate = async (...args) => {
      const params = args as [any];
      if (listeners.length > 0 && params[0].pathname !== '/') {
        const promises = listeners.map((fn) => fn());
        const values = await Promise.all(promises);
        const allowed = values.every(Boolean);
        if (!allowed) return;
      }
      return _navigate(...params);
    };
  }
  renderCounter.current = renderCounter.current + 1;
};
